import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as authApi from '../api/authApi';
import type { CurrentUser, LoginInput, RegistrationInput } from '../model/types';

export const sessionKey = ['auth', 'session'] as const;

/**
 * Kim jestem. Wołane raz przy starcie aplikacji, żeby odtworzyć sesję po
 * odświeżeniu strony — ciasteczko przeżywa przeładowanie, pamięć Reacta nie.
 *
 * `null` to poprawny wynik („nikt nie jest zalogowany"), a nie brak danych.
 */
export function useSession() {
  return useQuery<CurrentUser | null>({
    queryKey: sessionKey,
    queryFn: ({ signal }) => authApi.fetchCurrentUser(signal),
    // Sesja nie zmienia się sama z siebie; zmienia ją logowanie i wylogowanie,
    // a oba i tak unieważniają ten klucz jawnie.
    staleTime: Infinity,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: (user) => {
      // Odpowiedź logowania JEST już opisem zalogowanego, więc wstawiamy ją
      // wprost zamiast wołać `/auth/me` drugi raz.
      queryClient.setQueryData(sessionKey, user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      // ⚠ `onSettled`, nie `onSuccess`. Jeśli wylogowanie padnie na sieci,
      // ciasteczko i tak mogło zostać unieważnione po stronie serwera —
      // trzymanie użytkownika w stanie „zalogowany" byłoby wtedy kłamstwem.
      queryClient.setQueryData(sessionKey, null);
      // Cały cudzy stan wypada razem z sesją: dane jednego użytkownika nie mogą
      // przeciec do widoku następnego, który zaloguje się w tej samej karcie.
      queryClient.clear();
    },
  });
}

export function useRegister(token: string) {
  return useMutation({
    mutationFn: (input: RegistrationInput) => authApi.register(token, input),
  });
}
